import { useEffect } from 'react';
import { activeRows, shelfMatcher, type MatchableItem } from '../services/pantryMatch';
import { mealIdentityOf, type IdentifiableRecipe } from '../services/mealIdentity';

/** How long one slice may hold the main thread when the browser gives no idle deadline. A frame is 16 ms. */
const SLICE_MS = 10;
/** The longest a slice waits for an idle moment before running anyway. */
const IDLE_TIMEOUT_MS = 50;

type Slice = (budgetMs: number) => void;

/** Runs `fn` at the next idle moment, or after `IDLE_TIMEOUT_MS`; returns a cancel. */
const yieldToIdle = (fn: Slice): (() => void) => {
  if (typeof requestIdleCallback === 'function') {
    const id = requestIdleCallback(
      (deadline) => fn(deadline.didTimeout ? SLICE_MS : Math.max(4, Math.min(deadline.timeRemaining(), 40))),
      { timeout: IDLE_TIMEOUT_MS },
    );
    return () => cancelIdleCallback(id);
  }
  const timer = setTimeout(() => fn(SLICE_MS), 32);
  return () => clearTimeout(timer);
};

/**
 * Walks the library against the shelf in idle slices, so the first
 * « Remplacer » after boot or a pantry change does not do it on the tap (#865).
 *
 * `shelfMatcher` keeps every line's answer per shelf; the fan, side and
 * preference rankings all read that one matcher, so warming it here warms
 * them all. On the founder household (1716 recipes × 711 rows) the walk is
 * ~0.8 s cold in Node and ~2 s on a throttled phone; in slices of a few ms it
 * never holds a tap, and a tap that lands mid-walk only finds the rest of the
 * library still to resolve. A changed library or shelf cancels the walk and
 * starts over (the lines already answered stay answered).
 */
export function usePrewarmPantryMatcher<R extends { ingredients?: string[] }>(
  recipes: readonly R[],
  inventory: readonly MatchableItem[],
  also?: {
    /**
     * The planned meals' recipes: the same-meal history reads their identity
     * on every opening, and a live-sync snapshot recreates the objects.
     */
    history?: readonly IdentifiableRecipe[];
    /** The cook's score, so its taste and pantry parts are answered before the tap. */
    score?: (recipe: R) => number;
  },
): void {
  const history = also?.history;
  const score = also?.score;
  useEffect(() => {
    const shelf = activeRows(inventory);
    if (shelf.length === 0 || recipes.length === 0) return undefined;
    const matcher = shelfMatcher(shelf);
    let cancelled = false;
    let cancelIdle: (() => void) | null = null;
    let h = 0;
    let i = 0;
    const slice: Slice = (budgetMs) => {
      cancelIdle = null;
      if (cancelled) return;
      const deadline = performance.now() + budgetMs;
      while (history && h < history.length && performance.now() < deadline) {
        mealIdentityOf(history[h]);
        h += 1;
      }
      while (i < recipes.length && performance.now() < deadline) {
        matcher.itemsUsedBy(recipes[i]);
        score?.(recipes[i]);
        i += 1;
      }
      if ((history && h < history.length) || i < recipes.length) cancelIdle = yieldToIdle(slice);
    };
    cancelIdle = yieldToIdle(slice);
    return () => {
      cancelled = true;
      cancelIdle?.();
    };
  }, [recipes, inventory, history, score]);
}
